import React, { useState, useEffect, useRef } from "react";
import styles from "../styles/chatComp.module.css";
import TravelCard from "./componetsChat/comp1";
import LocationComp from "./componetsChat/comp2";
import TrainCard from "./componetsChat/comp3";
import FlightCard from "./componetsChat/comp4";
import { Button } from "./ui/button";

const ChatComp = ({
  data,
  showMoreData,
  handleShowMore,
  isPaginating,
  selectedTravelOption,
  onTravelOptionSelect,
}) => {
  const [selectedTimelineSteps, setSelectedTimelineSteps] = useState([]);
  const [selected, setSelected] = useState(0);
  const containerRef = useRef(null);
  const messageContainerRef = useRef(null);
  const showMoreRef = useRef(null);
  const scrollPositionRef = useRef(null);

  // Update selected timeline steps when data or selection changes
  useEffect(() => {
    if (!data || data.length === 0) {
      setSelectedTimelineSteps([]);
      return;
    }

    if (selectedTravelOption) {
      // Find the index of the selected item
      const selectedIndex = data.findIndex(item =>
        item.departure?.time === selectedTravelOption.departure?.time &&
        item.arrival?.time === selectedTravelOption.arrival?.time &&
        item.price === selectedTravelOption.price
      );

      if (selectedIndex !== -1) {
        // Update timeline steps for the selected item
        setSelectedTimelineSteps(data[selectedIndex].timeline_steps || []);
      } else {
        // If selected item not found, reset to first item
        setSelectedTimelineSteps(data[0].timeline_steps || []);
        onTravelOptionSelect(data[0]);
      }

    } else {
      // If no selection, default to first item
      setSelectedTimelineSteps(data[0].timeline_steps || []);
      onTravelOptionSelect(data[0]);
    }

    for (let i = 0; i < data.length; i++) {
      if (i == selected) {
        setSelectedTimelineSteps(data[i].timelineSteps);
        break;
      }
    }
  }, [data, selectedTravelOption, onTravelOptionSelect]);

  // Handle travel card click
  const handleTravelCardClick = (item, index) => {
    setSelectedTimelineSteps(item.timelineSteps);
    setSelected(index);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle show more click
  const handleShowMoreClick = (key, value) => {
    // Store current scroll position
    const chatHistory = document.querySelector('.chat-history');
    if (chatHistory) {
      scrollPositionRef.current = chatHistory.scrollTop;
    }

    handleShowMore(key, value);

    // Scroll to the bottom of this message container after a short delay
    setTimeout(() => {
      if (showMoreRef.current && chatHistory) {
        const messageBottom = showMoreRef.current.getBoundingClientRect().bottom;
        const chatHistoryTop = chatHistory.getBoundingClientRect().top;
        const relativeBottom = messageBottom - chatHistoryTop;

        // Calculate new scroll position
        const newScrollTop = chatHistory.scrollTop + relativeBottom - chatHistory.clientHeight + 50;

        // Scroll to new position
        chatHistory.scrollTo({
          top: newScrollTop,
          behavior: 'smooth'
        });

        // Prevent chat from scrolling to end
        const preventScroll = () => {
          if (chatHistory.scrollTop > newScrollTop + 100) {
            chatHistory.scrollTop = newScrollTop;
          }
        };

        // Add scroll event listener
        chatHistory.addEventListener('scroll', preventScroll);

        // Remove scroll event listener after animation completes
        setTimeout(() => {
          chatHistory.removeEventListener('scroll', preventScroll);
        }, 1000);
      }
    }, 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setSelectedTimelineSteps([]);
    };
  }, []);

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <div className={styles["main-content"]} ref={containerRef}>
      <div className={styles["container1"]} ref={messageContainerRef}>
        <div className={styles["card-wrapper"]}>
          {data.map((item, index) => {
            const isSelected = index == selected;
            // Create a more unique key using index and multiple properties
            const uniqueKey = `travel-${index}-${item.departure?.time || ''}-${item.arrival?.time || ''}-${item.price || ''}-${Date.now()}`;

            return (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ flex: 1 }}>
                  <TravelCard
                    key={uniqueKey}
                    tag={item.tag}
                    departure={item.departure}
                    arrival={item.arrival}
                    duration={item.duration}
                    stops={item.stops}
                    price={item.price}
                    currency={item.currency}
                    availability={item.availability}
                    modes={item.modes}
                    toolTip={item.toolTip}
                    transportModes={item.transportModes}
                    onClick={() => handleTravelCardClick(item, index)}
                    isSelected={isSelected}
                  />
                </div>
                {/* {isSelected && (
                  <span className={styles.selectedArrow}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <polygon points="8,4 20,12 8,20" fill="#FF670A" />
                    </svg>
                  </span>
                )} */}
              </div>
            );
          })}
        </div>

        {showMoreData && (
          <div className={styles["show-more-container"]} ref={showMoreRef}>
            <Button
              onClick={() => handleShowMoreClick(showMoreData.key, showMoreData.value)}
              disabled={isPaginating}
            >
              {isPaginating ? (
                <div className={styles["button-loader"]}>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                "Show More"
              )}
            </Button>
          </div>
        )}
      </div>

      <div className={styles["container2"]}>
        <div className={styles["timeline-list"]}>
          {selectedTimelineSteps?.map((step, idx) => {
            // Create a more unique key for timeline steps
            const stepKey = `timeline-${idx}-${step.type}-${step.data?.trainName || step.data?.name || ''}-${step.data?.from?.time || ''}-${step.data?.to?.time || ''}-${Date.now()}`;
            return (
              <div
                className={`${styles["timeline-step"]} ${idx === 0
                  ? styles["first"]
                  : idx === selectedTimelineSteps.length - 1
                    ? styles["last"]
                    : ""
                  }`}
                key={stepKey}
              >
                <div className={styles["timeline-icon-col"] + (selectedTimelineSteps.length === 1 ? ` ${styles["single-component"]}` : "")}>
                  <div className={styles["timeline-icon"]}>{step.icon}</div>
                </div>
                <div className={styles["timeline-content"]}>
                  {step.type === "TrainCard" && <TrainCard {...step.data} />}
                  {step.type === "LocationComp" && <LocationComp {...step.data} />}
                  {step.type === "FlightCard" && <FlightCard {...step.data} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatComp;